import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useRef } from "react";
import { L10n } from "@fidget/common/i18n";
import { noop } from "@fidget/common/utils";
import { HOUR_MILLIS, TimeUtils } from "@fidget/common/utils/timeUtils";
import { ICalendarMeeting } from "../../models/calendar/CalendarMeeting";
import styles from "./calendarCell.css";
import { useStore } from "../../models";
import { CalendarCellMeetingTime } from "./calendarCellMeetingTime";
import { CalendarCellMeetingClient } from "./calendarCellMeetingClient";

interface Props {
  time: Date;
  outsideWorkingHours: boolean;
}

export const CalendarCell = observer(({ time, outsideWorkingHours }: Props) => {
  const { calendarStore } = useStore();
  const calendarRow = useRef<HTMLElement>();

  const isEndTimeOutOfRange = (start: Date, duration: number, fillPointer: boolean): boolean => {
    const endTime = new Date(start.getTime() + duration);
    if (endTime.getDate() > start.getDate()) {
      if (!fillPointer) {
        return endTime.getMinutes() > 0;
      }
      return true;
    }
    return false;
  };

  const render = (): JSX.Element => {
    const { draggingMeeting } = calendarStore;

    const duration =
      (draggingMeeting && draggingMeeting.meeting && draggingMeeting.meeting.duration) ||
      HOUR_MILLIS;
    const otherMeetings = calendarStore.meetings.filter(
      (m: ICalendarMeeting) => m !== (draggingMeeting && draggingMeeting.meeting)
    );
    const overlaps = TimeUtils.overlapsWithMeeting(otherMeetings, time, duration);
    const cellHeight = calendarRow.current
      ? ((4 * duration) / HOUR_MILLIS) * calendarRow.current.offsetHeight - 4 + "px"
      : (400 * duration) / HOUR_MILLIS + "%";
    const sizeClasses = duration === HOUR_MILLIS / 4 ? [styles["one-line"]] : [];

    return (
      <div
        className={classNames(styles["calendar-row-inner"], {
          [styles["outside-working-hours"]]: outsideWorkingHours
        })}
        onMouseEnter={
          isEndTimeOutOfRange(time, duration, false)
            ? noop
            : () => draggingMeeting && draggingMeeting.mouseEnteredCalendarEntry(time)
        }
        onMouseLeave={() => draggingMeeting && draggingMeeting.mouseLeftCalendarEntry()}
        ref={(el) => (calendarRow.current = el || undefined)}
      >
        {draggingMeeting &&
          draggingMeeting.calendarPosition &&
          draggingMeeting.calendarPosition.getTime() === time.valueOf() &&
          ((time.valueOf() <= Date.now() && (
            <div
              className={classNames([
                styles.meeting,
                styles["calendar-snapped"],
                styles["meeting-invalid"],
                sizeClasses
              ])}
              style={{ height: cellHeight }}
            >
              <L10n id={"calendar.chooseFutureDate"} />
            </div>
          )) ||
            (overlaps && (
              <div
                className={[
                  styles.meeting,
                  styles["calendar-snapped"],
                  styles["meeting-invalid"],
                  sizeClasses
                ].join(" ")}
                style={{ height: cellHeight }}
              >
                <L10n id={"calendar.slotBooked"} />
              </div>
            )) || (
              <div
                className={classNames([
                  isEndTimeOutOfRange(draggingMeeting.calendarPosition, duration, true)
                    ? styles.pointer
                    : "",
                  sizeClasses,
                  styles.meeting,
                  styles["calendar-snapped"]
                ])}
                style={{ height: cellHeight }}
              >
                <CalendarCellMeetingTime
                  start={draggingMeeting.calendarPosition}
                  duration={duration}
                />
                <CalendarCellMeetingClient guest={draggingMeeting.guest} />
              </div>
            ))}
      </div>
    );
  };

  return render();
});
